import type { Type } from '@angular/core';
import { Routes } from '@angular/router';
import { lazyProvider } from '@ngxs/store';

import { GreetingCanMatchGuard, LoginGuard, PagesCanLoadGuard } from '@efficiently/client-core-guards';
import { APP_ROOT_URL, APP_ROUTES } from '@efficiently/client-core-routing';
import { mobileGuard } from '@efficiently/client-domain-core';
import {
	PORTAL_SESSION_STATE_PROVIDERS,
	portalAuthGuard,
	portalSessionWarmupResolver,
	providePortalLoginRedirects,
} from '@efficiently/client-domain-portal-auth';
import { PORTAL_PROJECTS_REPOSITORY_PROVIDERS } from '@efficiently/client-domain-project-picker';
import { CP_PROJECT_PICKER_PROVIDERS } from '@efficiently/client-feat-project-picker';
import { uuidToShort } from '@efficiently/client-util-helpers';

import type { DesignSectionRouteOptions } from './design-section-route-options.model';
import { CLIENT_APP_PORTAL_ROUTES_CONST } from './pages/client-app-portal/client-app-portal-routes.const';
import { otpLoginGuard } from './pages/otp-login/otp-login.guard';

/** Wraps the shell's children in whatever route a build needs between the shell and its sections. */
type ShellChildrenWrapper = (children: Routes) => Routes;

/** The default: the children are mounted on the shell exactly as they were built. */
const noChildrenWrapper: ShellChildrenWrapper = children => children;

/**
 * The application's route table, over the shell that hosts the sections and whatever sections that
 * shell adds beyond Design.
 *
 * A function rather than a constant because the two builds differ in exactly these arguments,
 * and each passes its own from its own entry point — `design.routes.ts` and `platform.routes.ts`.
 * Naming either shell here would put both into every bundle: a module-level import is resolved
 * statically, so the platform chrome would ship with the build that renders none of it.
 *
 * `wrapChildren` is the fourth of those arguments, and it exists so that a build can put ONE route
 * between the shell and every section it hosts without this file naming what that route carries. The
 * build with the Control Center passes a lazily loaded Clarity environment — one session, one realtime
 * relay, one domain context above both sections, plus the AI Chat panel's content — and the build
 * without it passes nothing, so its Design child stays a direct child of the shell. Keeping the
 * wrapper a parameter is what keeps that environment behind `platform.routes.ts`'s own `import()`
 * rather than in this table's static graph (boundary 1's TR-8).
 */
export function createAppRoutes(
	shell: Type<unknown>,
	sectionRoutes: Routes = [],
	designSection: DesignSectionRouteOptions = {},
	wrapChildren: ShellChildrenWrapper = noChildrenWrapper,
): Routes {
	return [
		{
			path: APP_ROUTES.GREETING.path,
			loadComponent: () => import('./core/components/greeting/greeting.component').then(m => m.GreetingComponent),
			canMatch: [GreetingCanMatchGuard],
		},
		{
			path: APP_ROUTES.MAINTENANCE.path,
			loadComponent: () =>
				import('./core/components/maintenance/maintenance.component').then(m => m.MaintenanceComponent),
		},
		{
			path: APP_ROUTES.LOGIN.path,
			loadComponent: () => import('./pages/login').then(m => m.LoginComponent),
			canActivate: [LoginGuard],
		},
		{
			path: APP_ROUTES.LOGOUT.path,
			loadComponent: () => import('./pages/logout').then(m => m.LogoutComponent),
		},
		{
			path: APP_ROUTES.CHANGE_EMAIL.path,
			loadComponent: () => import('./pages/login').then(m => m.LoginComponent),
		},
		{
			path: APP_ROUTES.REGISTER.path,
			loadComponent: () => import('./pages/register').then(m => m.RegisterComponent),
			canActivate: [LoginGuard],
		},
		{
			path: APP_ROUTES.FORGOT_PASSWORD.path,
			loadComponent: () => import('./pages/forgot-password').then(m => m.ForgotPasswordComponent),
		},
		{
			path: APP_ROUTES.RESET_PASSWORD.path,
			loadComponent: () => import('./pages/reset-password').then(m => m.ResetPasswordComponent),
		},
		{
			path: APP_ROUTES.SUBSCRIPTION.path,
			loadComponent: () => import('./pages/subscription').then(m => m.SubscriptionComponent),
		},
		{
			path: APP_ROUTES.SCOPE_LOGIN.path,
			loadComponent: () => import('./pages/scope-login/scope-login.component').then(m => m.ScopeLoginComponent),
		},
		{
			path: APP_ROUTES.OTP_LOGIN.path,
			loadComponent: () => import('./pages/otp-login/otp-login.component').then(m => m.OtpLoginComponent),
			canActivate: [otpLoginGuard],
		},
		{
			path: APP_ROUTES.REQUEST_ACCESS_SCOPE.path,
			loadComponent: () =>
				import('./pages/request-access-scope/request-access-scope.component').then(
					m => m.RequestAccessScopeComponent,
				),
		},
		{
			path: `${APP_ROUTES.WEB_HOSTED_SCOPE.path}/:projectId`,
			loadComponent: () =>
				import('./pages/web-hosted-scope/web-hosted-scope-mobile/web-hosted-scope-mobile.component').then(
					m => m.WebHostedScopeMobileComponent,
				),
			canMatch: [mobileGuard],
			loadChildren: () =>
				import('./pages/web-hosted-scope/web-hosted-scope-mobile/web-hosted-scope-mobile.routes').then(
					m => m.WEB_HOSTED_SCOPE_MOBILE_ROUTES,
				),
		},
		{
			path: `${APP_ROUTES.WEB_HOSTED_SCOPE.path}/:projectId`,
			loadComponent: () =>
				import('./pages/web-hosted-scope/web-hosted-scope/web-hosted-scope.component').then(
					m => m.WebHostedScopeComponent,
				),
		},
		{
			path: APP_ROUTES.REQUEST_ACCESS_PORTAL.path,
			providers: [...CP_PROJECT_PICKER_PROVIDERS, ...PORTAL_SESSION_STATE_PROVIDERS],
			resolve: [portalSessionWarmupResolver],
			loadComponent: () =>
				import('@efficiently/client-feat-request-access-portal').then(m => m.RequestAccessPortalComponent),
		},
		{
			path: APP_ROUTES.PORTAL_LOGIN.path,
			providers: [
				...PORTAL_PROJECTS_REPOSITORY_PROVIDERS,
				...providePortalLoginRedirects({
					singleProject: (projectId: string) =>
						CLIENT_APP_PORTAL_ROUTES_CONST.ROOMS.fullPath(uuidToShort(projectId)),
					multiProject: () => `/${APP_ROUTES.REQUEST_ACCESS_PORTAL.path}`,
				}),
				...PORTAL_SESSION_STATE_PROVIDERS,
			],
			loadComponent: () => import('@efficiently/client-feat-portal-login').then(m => m.PortalLoginComponent),
		},
		{
			path: APP_ROUTES.CLIENT_APP_PORTAL.path,
			providers: [...PORTAL_SESSION_STATE_PROVIDERS],
			canActivate: [portalAuthGuard({ redirectOnFail: `/${APP_ROUTES.PORTAL_LOGIN.path}` })],
			loadChildren: () =>
				import('./pages/client-app-portal/client-app-portal.routes').then(m => m.CLIENT_APP_PORTAL_ROUTES),
		},
		{
			path: APP_ROUTES.PAGES.path,
			canActivate: [
				PagesCanLoadGuard,
				lazyProvider(() => import('./shell/shell-states').then(m => m.shellStateProvider)),
			],
			component: shell,
			children: wrapChildren([
				...sectionRoutes,
				{
					path: APP_ROUTES.PAGES.path,
					...designSection,
					loadChildren: () => import('./pages/pages.routes').then(m => m.routes),
				},
			]),
		},
		{
			path: '**',
			redirectTo: APP_ROOT_URL,
		},
	];
}
